import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, MailX, UserCheck } from "lucide-react";
import { errorMessage, get, post } from "@/lib/api";
import { TEAM_ROLE_LABELS } from "@/lib/constants";
import { usePageTitle } from "@/components/States";
import { Brand } from "@/components/layout/SidebarShell";
import { Button } from "@/components/ui/button";

const Card = ({ children, testId }) => (
  <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-xl" data-testid={testId}>
    <div className="mb-5 inline-block rounded-lg bg-slate-900 p-3"><Brand roleLabel="For warrant teams" /></div>
    {children}
  </div>
);

export default function AcceptInvite() {
  usePageTitle("Accept invite");
  const [params] = useSearchParams();
  const token = params.get("token");
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(token ? "" : "This link is missing its token. Ask your manager for a new one.");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      return;
    }
    get(`/invites/accept/${encodeURIComponent(token)}`).then(setInvite).catch((e) => setError(errorMessage(e)));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await post(`/invites/accept/${encodeURIComponent(token)}`);
      setDone(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const signIn = <Link to="/login" className="mt-5 inline-block text-sm font-semibold text-blue-700 hover:underline" data-testid="accept-invite-login-link">Go to sign in</Link>;
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-6">
      {error ? (
        <Card testId="accept-invite-error"><MailX className="mx-auto h-10 w-10 text-rose-600" /><h1 className="mt-3 font-display text-xl font-bold text-slate-900">Invite not available</h1><p className="mt-2 text-sm text-slate-600">{error}</p>{signIn}</Card>
      ) : done ? (
        <Card testId="accept-invite-done"><CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" /><h1 className="mt-3 font-display text-xl font-bold text-slate-900">You&apos;ve joined {invite.company_name}</h1><p className="mt-2 text-sm text-slate-600">Sign in with your usual email and password to see your jobs.</p>{signIn}</Card>
      ) : !invite ? (
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" data-testid="accept-invite-loading" />
      ) : (
        <Card testId="accept-invite-card">
          <UserCheck className="mx-auto h-10 w-10 text-blue-700" />
          <h1 className="mt-3 font-display text-xl font-bold text-slate-900">Join {invite.company_name}</h1>
          <p className="mt-2 text-sm text-slate-600">{invite.full_name ? `Hi ${invite.full_name}, you've` : "You've"} been invited to join {invite.company_name} as a {TEAM_ROLE_LABELS[invite.role] || invite.role}.</p>
          <Button onClick={accept} disabled={busy} className="mt-5 h-11 w-full bg-blue-700 hover:bg-blue-800" data-testid="accept-invite-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Accept invite
          </Button>
        </Card>
      )}
    </div>
  );
}
